import { isBrowser } from '@tbitop/ssr';
import { isNull } from 'lodash-es';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { log } from './log';
import type { TelemetryConfig } from './telemetry';
import { Telemetry } from './telemetry';

const TelemetryContext = createContext<Telemetry | null>(null);

type TelemetryProviderProps = {
  config: TelemetryConfig;
};

export const TelemetryProvider = ({ children, config }: PropsWithChildren<TelemetryProviderProps>) => {
  const [telemetry, setTelemetry] = useState<Telemetry | null>(null);
  const { instrumentationKey } = config;

  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    if (isNull(instrumentationKey)) {
      log.trace('Telemetry disabled. No instrumentation key provided.');
      setTelemetry(null);
      return;
    }

    log.trace(`Instantiating Telemetry with instrumentation key ${instrumentationKey}.`);
    const instance = new Telemetry(instrumentationKey);
    setTelemetry(instance);

    return () => {
      instance.destroy();
      setTelemetry(null);
    };
  }, [instrumentationKey]);

  return (
    <TelemetryContext.Provider value={telemetry}>
      {children}
    </TelemetryContext.Provider>
  );
};

export const useTelemetry = () => {
  return useContext(TelemetryContext);
};

export type WithTelemetryProps = {
  telemetry: Telemetry | null;
};

export function withTelemetry<T extends WithTelemetryProps = WithTelemetryProps>(
  WrappedComponent: React.ComponentType<T>,
) {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithTelemetry = (props: Omit<T, keyof WithTelemetryProps>) => {
    // Fetch the props you want to inject. This could be done with context instead.
    const telemetryProps = { telemetry: useTelemetry() };

    // props comes afterwards so the can override the default ones.
    return <WrappedComponent {...telemetryProps} {...(props as T)} />;
  };

  ComponentWithTelemetry.displayName = `withTelemetry(${displayName})`;

  return ComponentWithTelemetry;
}
